// components/SEO.jsx - Tunisia-Optimized SEO Component

import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEO = ({ 
  title = 'Reyza - Shopping en Ligne en Tunisie',
  description = 'Achetez les meilleurs produits aux meilleurs prix en Tunisie. Livraison gratuite, paiement securise.',
  keywords = 'shopping en ligne tunisie, e-commerce tunisie, acheter en ligne, produits tunisie',
  image = null,
  url = '',
  type = 'website',
  structuredData = null,
  noIndex = false,
  noFollow = false,
  product = null,
  category = null,
  breadcrumbs = [],
  includeOrganization = false,
  locale = 'fr_TN',
  alternateLocales = ['ar_TN', 'en_US'],
  twitterHandle = '@reyza'
}) => {
  const siteUrl = process.env.REACT_APP_SITE_URL || 'http://localhost:3000';
  const siteName = process.env.REACT_APP_SITE_NAME || 'Reyza';
  const fullUrl = url ? `${siteUrl}${url}` : siteUrl;
  
  const defaultImage = `${siteUrl}/logo512.png`;
  const fullImage = image ? (image.startsWith('http') ? image : `${siteUrl}${image}`) : defaultImage;

  // Tunisia-specific product schema
  const generateProductSchema = () => {
    if (!product) return null;

    return {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": product.name,
      "image": product.imageUrl || fullImage,
      "description": product.shortDescription || product.longDescription || description,
      "sku": `REYZA-${product.id}`,
      "brand": {
        "@type": "Brand",
        "name": siteName
      },
      "offers": {
        "@type": "Offer",
        "url": fullUrl,
        "priceCurrency": "TND", // Tunisian Dinar
        "price": product.salePrice || product.regularPrice,
        "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
        "seller": {
          "@type": "Organization",
          "name": siteName
        },
        "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
        "itemCondition": "https://schema.org/NewCondition"
      },
      ...(product.averageRating && product.totalReviews > 0 && {
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": parseFloat(product.averageRating).toFixed(1),
          "reviewCount": product.totalReviews
        }
      })
    };
  };

  const generateCategorySchema = () => {
    if (!category) return null;

    return {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": category.name,
      "description": category.description || description,
      "url": fullUrl,
      ...(category.imageUrl && { "image": category.imageUrl })
    };
  };

  const generateBreadcrumbSchema = () => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null;

    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url.startsWith('http') ? crumb.url : `${siteUrl}${crumb.url}`
      }))
    };
  };

  const generateOrganizationSchema = () => {
    if (!includeOrganization) return null;
    return {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": siteName,
      "url": siteUrl,
      "logo": `${siteUrl}/logo512.png`,
      "sameAs": [
        process.env.REACT_APP_FB_URL || '',
        process.env.REACT_APP_IG_URL || '',
        process.env.REACT_APP_LINKEDIN_URL || ''
      ].filter(Boolean)
    };
  };

  const schemas = [
    structuredData,
    generateProductSchema(),
    generateCategorySchema(),
    generateBreadcrumbSchema(),
    generateOrganizationSchema()
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={locale.split('_')[0]} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={fullUrl} />
      
      {/* Geographic Targeting */}
      <meta name="geo.region" content="TN" />
      <meta name="geo.placename" content="Tunisia" />
      <meta name="language" content={locale.startsWith('ar') ? 'Arabic' : 'French'} />
      
      {/* Robots */}
      {(noIndex || noFollow) && (
        <meta name="robots" content={`${noIndex ? 'noindex' : 'index'},${noFollow ? 'nofollow' : 'follow'}`} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={locale} />
      {alternateLocales.map(loc => (
        <meta key={loc} property="og:locale:alternate" content={loc} />
      ))}
      {alternateLocales.map(loc => (
        <link key={`alt-${loc}`} rel="alternate" hrefLang={loc.replace('_', '-')} href={fullUrl} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content={twitterHandle} />

      {/* Structured Data (JSON-LD) */}
      {schemas.map((schema, idx) => (
        <script key={idx} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {/* Product Meta - Tunisia */}
      {product && (
        <>
          <meta property="product:price:amount" content={product.salePrice || product.regularPrice} />
          <meta property="product:price:currency" content="TND" />
          {product.stock > 0 && <meta property="product:availability" content="in stock" />}
        </>
      )}
    </Helmet>
  );
};

export default SEO;
